import React, { useState } from 'react';
import AdvancedPriceChartWidget from '../DashboardBuilder/AdvancedPriceChartWidget';
import EditHoldingModal from './EditHoldingModal';
import {
  calculateTargetProgress,
  formatRelativeTime,
  formatCurrency,
  formatPercentWithSign,
  getValueColorClass,
} from '../../utils/contractCalculations';
import { positionValues } from './portfolioConstants';

/**
 * Card for a plain equity holding, mirroring WatchlistContractCard's layout so
 * the portfolio grid reads as one thing even when it mixes stock and options.
 *
 * Stock is tracking-only: there is no premium to project, no Greeks, and no
 * expiration, so this card carries a single Price History tab rather than the
 * option card's Overview / Charts / Simulated Returns.
 */
export default function StockHoldingCard({
  contract,
  isSelected,
  onSelect,
  onRefresh,
  onDelete,
  onContractUpdate,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState('1y');

  const {
    id,
    ticker,
    number_of_contracts = 0,
    average_cost_per_contract = 0,
    current_premium = 0,
    last_refresh_date,
    underlying_price_at_add = 0,
    current_underlying_price = 0,
    target_percent_change,
  } = contract;

  const { costBasis, marketValue, pnl, pnlPercent, isShort } = positionValues(contract);

  const targetProgress = calculateTargetProgress(
    underlying_price_at_add,
    current_underlying_price,
    target_percent_change
  );

  const handleSelectChange = (e) => {
    e.stopPropagation();
    onSelect(id, e.target.checked);
  };

  const handleCardClick = () => setIsExpanded((prev) => !prev);

  const renderProgressBar = () => {
    if (!targetProgress) return null;
    const { actualChange, targetChange, progress } = targetProgress;
    const progressPercent = Math.min(Math.max(progress, 0), 100);
    const isNegative = progress < 0;

    return (
      <div className="space-y-1">
        <div className="relative w-full bg-[color:var(--pf-control)] rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${
              isNegative ? 'bg-red-500' : 'bg-green-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
          {progress > 100 && (
            <div className="absolute -right-1 -top-1 text-yellow-400 text-xs">⚡</div>
          )}
        </div>
        <div className={`text-[11px] ${getValueColorClass(actualChange)}`}>
          {formatPercentWithSign(actualChange, 1)} of {formatPercentWithSign(targetChange, 0)}
        </div>
      </div>
    );
  };

  const renderCompactCard = () => (
    <div className="cursor-pointer transition-all duration-200" onClick={handleCardClick}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelectChange}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 mt-1"
          />
          <div className="text-brand-white font-medium text-sm leading-tight">
            {ticker}{' '}
            <span className="text-[color:var(--pf-muted)]">{isShort ? 'SHORT' : 'LONG'}</span>{' '}
            <span className="px-1.5 py-0.5 rounded bg-[color:var(--pf-control)] text-[10px] text-[color:var(--pf-label)] align-middle">
              STOCK
            </span>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
          className="text-[#218aff] text-xs hover:text-blue-300 transition flex-shrink-0"
        >
          ▾
        </button>
      </div>

      <div className="text-xs text-[color:var(--pf-muted)] mb-3">
        📊 {number_of_contracts} share{number_of_contracts === 1 ? '' : 's'} @{' '}
        {formatCurrency(average_cost_per_contract)} avg
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
        <div>
          <div
            className="text-[color:var(--pf-faint)] text-[10px] mb-1 cursor-help"
            data-tooltip-id="portfolio-tooltip"
            data-tooltip-content="Latest market price per share."
          >
            Price
          </div>
          <div className="font-medium">{formatCurrency(current_premium)}</div>
        </div>

        <div>
          <div
            className="text-[color:var(--pf-faint)] text-[10px] mb-1 cursor-help"
            data-tooltip-id="portfolio-tooltip"
            data-tooltip-content="Shares x current price."
          >
            Market Value
          </div>
          <div className="font-medium">{formatCurrency(marketValue)}</div>
        </div>

        <div>
          <div
            className="text-[color:var(--pf-faint)] text-[10px] mb-1 cursor-help"
            data-tooltip-id="portfolio-tooltip"
            data-tooltip-content="Market value minus cost basis. Inverted for a short position, so positive always means a gain."
          >
            Unrealized P&L
          </div>
          <div className={`font-medium ${getValueColorClass(pnl)}`}>
            {formatCurrency(pnl)}
          </div>
          <div className={getValueColorClass(pnlPercent)}>
            {formatPercentWithSign(pnlPercent, 1)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-[color:var(--pf-faint)] text-[10px] mb-1">Cost Basis</div>
          <div className="text-[color:var(--pf-label)] text-[11px]">{formatCurrency(costBasis)}</div>
        </div>

        <div>
          <div
            className="text-[color:var(--pf-faint)] text-[10px] mb-1 cursor-help"
            data-tooltip-id="portfolio-tooltip"
            data-tooltip-content="How far the price has moved toward your target."
          >
            Target Progress
          </div>
          {targetProgress ? (
            renderProgressBar()
          ) : (
            <div className="text-[color:var(--pf-faint)] text-[10px]">No target set</div>
          )}
        </div>

        <div>
          <div className="text-[color:var(--pf-faint)] text-[10px] mb-1">Updated</div>
          <div className="text-[color:var(--pf-muted)] text-[10px]">
            {formatRelativeTime(last_refresh_date)}
          </div>
        </div>
      </div>

      <div
        className="text-xs text-[color:var(--pf-muted)] cursor-pointer hover:text-[#218aff] transition mt-2 pt-2 border-t border-[color:var(--pf-border)]"
        onClick={(e) => {
          e.stopPropagation();
          setEditModalOpen(true);
        }}
      >
        {target_percent_change != null
          ? `Target: ${formatPercentWithSign(target_percent_change, 0)}`
          : 'No Target Set - Click to Set'}
      </div>
    </div>
  );

  const renderExpandedCard = () => (
    <div className="space-y-3">
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-white/5 rounded px-2 py-1 transition"
        onClick={handleCardClick}
      >
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelectChange}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0"
          />
          <div className="text-brand-white font-medium text-sm">
            {ticker} {isShort ? 'SHORT' : 'LONG'}
            <span className="text-[color:var(--pf-muted)] text-xs ml-2">
              • {number_of_contracts} shares • {formatCurrency(marketValue)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <span className="text-xs text-[color:var(--pf-muted)]">Price:</span>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-[color:var(--pf-shell)] text-text text-xs px-2 py-1 rounded border border-[color:var(--pf-border)] hover:border-[#218aff] transition"
          >
            <option value="1m">1M</option>
            <option value="3m">3M</option>
            <option value="6m">6M</option>
            <option value="1y">1Y</option>
            <option value="2y">2Y</option>
            <option value="5y">5Y</option>
          </select>
          <button
            onClick={() => setEditModalOpen(true)}
            className="text-[color:var(--pf-muted)] hover:text-[#218aff] transition text-sm ml-2"
            title="Edit holding"
          >
            ⚙
          </button>
          <span className="text-[#218aff] text-xs cursor-pointer" onClick={handleCardClick}>
            ▲
          </span>
        </div>
      </div>

      <div className="bg-[color:var(--pf-shell)] rounded overflow-hidden">
        <div className="text-xs text-[color:var(--pf-muted)] px-3 py-2 border-b border-[color:var(--pf-border)] font-medium">
          Price History
        </div>
        <div className="h-[450px]">
          <AdvancedPriceChartWidget
            ticker={ticker}
            dateRange={dateRange}
            indicators={[]}
            livePrice={current_premium || null}
            liveTimestamp={last_refresh_date || null}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={`bg-[color:var(--pf-surface)] border rounded-lg p-3 transition-all duration-200 ${
          isSelected ? 'border-[#218aff] ring-2 ring-[#218aff]/50' : 'border-[color:var(--pf-border)] hover:border-[color:var(--pf-control-hover)]'
        } ${isExpanded ? 'col-span-full' : ''}`}
      >
        {!isExpanded ? renderCompactCard() : renderExpandedCard()}

        <div className="flex gap-2 mt-3 pt-2 border-t border-[color:var(--pf-border)]" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onRefresh?.(id)}
            className="btn-ghost-primary text-xs"
            title="Fetch the latest share price"
          >
            Refresh
          </button>
          <button
            onClick={() => setConfirmingDelete(true)}
            className="btn-ghost-danger text-xs"
            title="Remove this holding from your portfolio"
          >
            Remove
          </button>
        </div>
      </div>

      <EditHoldingModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        contract={contract}
        onSave={onContractUpdate}
      />

      {confirmingDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
          <div className="pf-modal p-6 w-80 text-center space-y-4">
            <p className="font-semibold text-white">
              Remove your {ticker} holding from the portfolio?
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  onDelete?.(id);
                  setConfirmingDelete(false);
                }}
                className="btn-danger"
              >
                Yes, Remove
              </button>
              <button onClick={() => setConfirmingDelete(false)} className="btn-dark">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
